
import React, { useEffect, useState } from "react";
import StepCard from "../StepCard";
import InstructionCheckbox from "./InstructionCheckbox";
import ImageWithFallback from "../ImageWithFallback";
import { Separator } from "@/components/ui/separator";

interface PressureAdvanceInstructionsProps {
  selectedSlicer: "creality" | "orca" | "anycubic";
  onNext: () => void;
  onPrevious: () => void;
}

const PressureAdvanceInstructions = ({
  selectedSlicer,
  onNext,
  onPrevious
}: PressureAdvanceInstructionsProps) => {
  // Get current theme from document
  const getCurrentTheme = () => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') || 'light';
    }
    return 'light';
  };

  const theme = getCurrentTheme();
  const [checkedSteps, setCheckedSteps] = useState<Record<string, boolean>>({});

  const handleStepCheck = (stepId: string, checked: boolean) => {
    setCheckedSteps(prev => ({
      ...prev,
      [stepId]: checked
    }));
  };

  const handleCheckAll = () => {
    const stepIds = [
      "pa-step1",
      "pa-step2", 
      "pa-step3",
      "pa-step4",
      "pa-step5",
      "pa-step6",
      "pa-step7"
    ];
    
    const allChecked = Object.fromEntries(stepIds.map(id => [id, true]));
    setCheckedSteps(prev => ({ ...prev, ...allChecked }));
  };

  // Auto-progress when all steps are checked
  useEffect(() => {
    const totalSteps = 7;
    const checkedCount = Object.values(checkedSteps).filter(Boolean).length;
    if (checkedCount === totalSteps) {
      setTimeout(() => {
        onNext();
      }, 500);
    }
  }, [checkedSteps, onNext]);

  const getSlicerName = () => {
    switch (selectedSlicer) {
      case "creality": return "Creality Print";
      case "orca": return "Orca";
      case "anycubic": return "Anycubic";
    }
  };

  const dropdownIcon = theme === "dark" 
    ? "/lovable-uploads/4b6c3e0e-5356-4046-931a-c1131e10ead9.png"
    : "/lovable-uploads/78941090-a5a7-4a41-969f-15cc7b0eba5e.png";

  return (
    <StepCard 
      title="" 
      onPrevious={onPrevious} 
      showPrevious 
      onCheckAll={handleCheckAll}
      showCheckAll
    >
      <div className="space-y-4">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
          <h4 className="font-semibold text-lg text-gray-900 dark:text-white">
            {getSlicerName()} Guide
          </h4>
        </div>
        
        <ol className="space-y-3">
          <InstructionCheckbox id="pa-step1" checked={checkedSteps["pa-step1"] || false} onCheckedChange={checked => handleStepCheck("pa-step1", checked)}>
            {selectedSlicer === "creality" ? (
              <span>
                Click the{" "}
                <ImageWithFallback src={dropdownIcon} alt="dropdown arrow" className="h-4 w-4 inline mx-1" />
                <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>dropdown arrow</span> in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>top left corner</span> of Creality Print, next to "<span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>File</span>"
              </span>
            ) : (
              <>Open {selectedSlicer === "orca" ? "Orca Slicer" : "Anycubic Next"} and load your printer profile</>
            )}
          </InstructionCheckbox>
          <Separator className="my-3 bg-gray-300" />
          
          <InstructionCheckbox id="pa-step2" checked={checkedSteps["pa-step2"] || false} onCheckedChange={checked => handleStepCheck("pa-step2", checked)}>
            Click <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Calibration</span> → <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Pressure Advance</span>
          </InstructionCheckbox>
          <Separator className="my-3 bg-gray-300" />
          
          <InstructionCheckbox id="pa-step3" checked={checkedSteps["pa-step3"] || false} onCheckedChange={checked => handleStepCheck("pa-step3", checked)}>
            Select <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>DDE</span> and <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>PA Tower</span>
          </InstructionCheckbox>
          <Separator className="my-3 bg-gray-300" />
          
          <InstructionCheckbox id="pa-step4" checked={checkedSteps["pa-step4"] || false} onCheckedChange={checked => handleStepCheck("pa-step4", checked)}>
            Set <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Start PA</span> to <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">0.02</span>
          </InstructionCheckbox>
          <Separator className="my-3 bg-gray-300" />
          
          <InstructionCheckbox id="pa-step5" checked={checkedSteps["pa-step5"] || false} onCheckedChange={checked => handleStepCheck("pa-step5", checked)}>
            Set <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>End PA</span> to <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">0.07</span>
          </InstructionCheckbox>
          <Separator className="my-3 bg-gray-300" />
          
          <InstructionCheckbox id="pa-step6" checked={checkedSteps["pa-step6"] || false} onCheckedChange={checked => handleStepCheck("pa-step6", checked)}>
            Set <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>PA step</span> to <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">0.002</span>
          </InstructionCheckbox>
          <Separator className="my-3 bg-gray-300" />
          
          <InstructionCheckbox id="pa-step7" checked={checkedSteps["pa-step7"] || false} onCheckedChange={checked => handleStepCheck("pa-step7", checked)}>
            <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Slice</span> and <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>print</span> the calibration tower
          </InstructionCheckbox>
        </ol>

        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className={`text-sm space-y-1 ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
            <p>Check all boxes to continue.</p>
          </div>
        </div>
      </div>
    </StepCard>
  );
};

export default PressureAdvanceInstructions;
